import React, { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { CreditCard, Loader2, RefreshCw } from 'lucide-react';
import { openCashfreeCheckout, getCashfree, feeBreakdown, FEE_PAYERS, PAYMENT_PROVIDERS, submitPayuForm } from '../utils/cashfree';
import { ApiService } from '../utils/api';
import { useToast } from './Toast';
import { formatCurrency } from '../utils/currency';

export const pendingRegistrationKey = (eventId) => `cashfree_registration_${eventId}`;

const readPendingRegistration = (eventId) => {
  try {
    return sessionStorage.getItem(pendingRegistrationKey(eventId));
  } catch (_) {
    return null;
  }
};

/**
 * Online payment via the event's gateway: Cashfree Drop-in, or a redirect to PayU's hosted checkout. The server
 * creates the registration (PENDING_PAYMENT), prices the order and confirms it from the gateway's status; the
 * browser never decides that a payment succeeded.
 */
const CashfreeCheckoutCard = ({
  eventId,
  eventName,
  currency = 'INR',
  formPayload,
  paymentConfig,
  source,
  notice,
  onPaid,
  onBack,
}) => {
  const [busy, setBusy] = useState(false);
  const [orderId, setOrderId] = useState(null);
  const [lastStatus, setLastStatus] = useState(null);
  const [serverAmounts, setServerAmounts] = useState(null);
  const { showError } = useToast();

  const usesPayu = paymentConfig?.provider === PAYMENT_PROVIDERS.PAYU;
  useEffect(() => {
    if (!usesPayu) getCashfree().catch(() => {});
  }, [usesPayu]);

  const estimate = feeBreakdown(paymentConfig, Number(formPayload?.totalPrice) || 0);
  const amounts = serverAmounts || { base: estimate.base, fee: estimate.fee, amount: estimate.charged };
  const showFee = amounts.fee > 0 && (paymentConfig?.feePayer || FEE_PAYERS.ATTENDEE) === FEE_PAYERS.ATTENDEE;

  const verifyAndFulfill = async (oid) => {
    const verify = await ApiService.verifyCashfreeOrderPublic(eventId, oid);
    setLastStatus(verify.orderStatus);
    if (verify.orderStatus === 'PAID') {
      onPaid?.(verify);
      return true;
    }
    return false;
  };

  const handlePay = async () => {
    setBusy(true);
    setLastStatus(null);
    try {
      const order = await ApiService.createCashfreeOrderPublic(eventId, {
        formData: formPayload?.formData,
        documentIds: formPayload?.documentIds || [],
        userId: formPayload?.userId,
        faceImageBase64: formPayload?.faceImage,
        couponHoldToken: formPayload?.couponHoldToken,
        inviteCode: formPayload?.inviteCode,
        accessCode: formPayload?.accessCode,
        source,
        registrationId: readPendingRegistration(eventId),
      });
      if (order.userId) {
        try {
          sessionStorage.setItem(pendingRegistrationKey(eventId), order.userId);
        } catch (_) {}
      }
      if (order.free) {
        onPaid?.({ userId: order.userId, orderStatus: 'PAID', registrationStatus: order.status, free: true, manageToken: order.manageToken });
        return;
      }
      setServerAmounts({ base: order.baseAmount, fee: order.platformFee, amount: order.amount });
      if (order.provider === PAYMENT_PROVIDERS.PAYU) {
        if (!order.payu?.action) throw new Error('Payment could not be started. Please try again.');
        // PayU returns the browser to /register/:eventId?order_id=… where the page verifies the payment.
        submitPayuForm(order.payu);
        return;
      }
      if (!order.paymentSessionId || !order.orderId) {
        throw new Error('Payment session was not created. Please try again.');
      }
      setOrderId(order.orderId);

      const { state } = await openCashfreeCheckout(order.paymentSessionId, order.cashfreeMode);
      if (state === 'redirect') return;
      const paid = await verifyAndFulfill(order.orderId);
      if (!paid) {
        showError(state === 'error' ? 'Payment was not completed. You can try again.' : 'Payment is still pending. If money was deducted, tap Recheck status.');
      }
    } catch (err) {
      showError(err?.message || 'Unable to start payment. Please try again.');
    } finally {
      setBusy(false);
    }
  };

  const handleRecheck = async () => {
    if (!orderId) return;
    setBusy(true);
    try {
      const paid = await verifyAndFulfill(orderId);
      if (!paid) showError('Payment not confirmed yet. If you paid, wait a moment and recheck.');
    } catch (err) {
      showError(err?.message || 'Could not verify payment.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="border-0 shadow-none bg-transparent">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl">
          <CreditCard className="w-5 h-5" />
          Pay online
        </CardTitle>
        <p className="text-sm text-gray-600">
          Secure checkout for <span className="font-medium">{eventName}</span>. Cards, UPI, net banking and wallets are
          available on the next screen. Your registration is confirmed as soon as the payment succeeds.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        {notice && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">{notice}</p>
        )}
        <div className="rounded-lg bg-gray-50 border p-4 space-y-2">
          {showFee && (
            <>
              <div className="flex justify-between text-sm text-gray-600"><span>Registration</span><span>{formatCurrency(amounts.base, currency)}</span></div>
              <div className="flex justify-between text-sm text-gray-600"><span>Convenience fee</span><span>{formatCurrency(amounts.fee, currency)}</span></div>
            </>
          )}
          <div className="flex items-center justify-between">
            <span className="text-gray-700">Amount payable</span>
            <span className="text-2xl font-semibold text-gray-900">{formatCurrency(amounts.amount, currency)}</span>
          </div>
        </div>

        {lastStatus && lastStatus !== 'PAID' && (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            Payment status: {lastStatus}. If money was deducted, tap Recheck status.
          </p>
        )}

        <div className="flex flex-col sm:flex-row gap-3">
          <Button onClick={handlePay} disabled={busy} className="flex-1" size="lg">
            {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing…</>) : (<><CreditCard className="w-4 h-4 mr-2" />Pay now</>)}
          </Button>
          {orderId && (
            <Button variant="outline" onClick={handleRecheck} disabled={busy}>
              <RefreshCw className="w-4 h-4 mr-2" />
              Recheck status
            </Button>
          )}
          {onBack && (
            <Button variant="ghost" onClick={onBack} disabled={busy}>Back</Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default CashfreeCheckoutCard;
